import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { patientPortal } from '@/api/patientPortal';
import PatientPortalPageHero from './PatientPortalPageHero';

type Member = {
  id: string;
  name: string;
  phone: string | null;
  uhid: string | null;
  relationship: string;
  direction: string;
};

const emptyFamily = {
  name: '',
  phone: '',
  dob: '',
  gender: 'MALE',
  relationship: 'PARENT_CHILD',
  email: '',
  address: '',
  preExistingCondition: '',
};

export default function PatientPortalFamily() {
  const [members, setMembers] = useState<Member[]>([]);
  const [poolHead, setPoolHead] = useState<{ id: string; name: string } | null>(null);
  const [familyForm, setFamilyForm] = useState(emptyFamily);
  const [familyMsg, setFamilyMsg] = useState('');

  const load = () => {
    patientPortal.getFamily().then((d: { members: Member[]; poolHead: { id: string; name: string } | null }) => {
      setMembers(d.members ?? []);
      setPoolHead(d.poolHead);
    });
  };

  useEffect(() => {
    load();
  }, []);

  const registerFamily = async () => {
    setFamilyMsg('');
    try {
      const r = await patientPortal.registerFamilyMember(familyForm);
      setFamilyMsg(
        `Registered ${r.name} in THMS (UHID ${r.uhid ?? 'pending'}) and linked as family.`
      );
      setFamilyForm(emptyFamily);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setFamilyMsg(err.response?.data?.message ?? 'Could not add family member');
    }
  };

  const field = (
    label: string,
    key: keyof typeof emptyFamily,
    type: string = 'text',
    required = false
  ) => (
    <label className="block text-sm">
      {label}
      {required ? ' *' : ''}
      <input
        type={type}
        className="mt-1 w-full border rounded px-3 py-2 text-sm"
        value={familyForm[key]}
        onChange={(e) => setFamilyForm((f) => ({ ...f, [key]: e.target.value }))}
        required={required}
      />
    </label>
  );

  return (
    <div className="space-y-10 max-w-xl">
      <div>
        <PatientPortalPageHero
          imageKey="family"
          eyebrow="Care circle"
          title="Family accounts"
          subtitle="Link loved ones and share loyalty points across your family pool"
          compact
        />
        <p className="text-sm text-muted-foreground mt-1">
          Add family members to THMS even if they are not registered yet. Enable points pooling on
          the Points page. To refer someone for a hospital visit, use the{' '}
          <Link to="/patient-loyalty/referrals" className="text-teal-700 underline">
            Referrals
          </Link>{' '}
          tab.
        </p>
      </div>

      {poolHead ? (
        <p className="text-sm bg-teal-50 border border-teal-100 rounded-lg p-3">
          Loyalty pool head: <strong>{poolHead.name}</strong>
        </p>
      ) : null}

      <ul className="border rounded-lg bg-white divide-y text-sm">
        {members.length === 0 ? (
          <li className="p-4 text-muted-foreground">No family members linked yet</li>
        ) : (
          members.map((m) => (
            <li key={`${m.id}-${m.direction}`} className="p-3">
              <p className="font-medium">{m.name}</p>
              <p className="text-muted-foreground text-xs mt-1">
                {m.relationship.replace(/_/g, ' ')} · {m.phone ?? '—'}
                {m.uhid ? ` · ${m.uhid}` : ''}
              </p>
            </li>
          ))
        )}
      </ul>

      <div className="border rounded-lg bg-white p-4 space-y-3">
        <h2 className="font-semibold">Add family member</h2>
        <p className="text-xs text-muted-foreground">
          Required fields match hospital registration. Each person gets their own UHID — the same
          mobile number can be used for children, parents, or others in your household. Add an email
          on their record so they can sign in with password or email OTP.
        </p>
        {field('Full name', 'name', 'text', true)}
        {field('Mobile (10 digits)', 'phone', 'tel', true)}
        {field('Date of birth', 'dob', 'date', true)}
        <label className="block text-sm">
          Gender *
          <select
            className="mt-1 w-full border rounded px-3 py-2 text-sm"
            value={familyForm.gender}
            onChange={(e) => setFamilyForm((f) => ({ ...f, gender: e.target.value }))}
          >
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        <label className="block text-sm">
          Relationship *
          <select
            className="mt-1 w-full border rounded px-3 py-2 text-sm"
            value={familyForm.relationship}
            onChange={(e) => setFamilyForm((f) => ({ ...f, relationship: e.target.value }))}
          >
            <option value="PARENT_CHILD">Parent / Child</option>
            <option value="SPOUSE">Spouse</option>
            <option value="SIBLING">Sibling</option>
          </select>
        </label>
        {field('Email', 'email', 'email')}
        {field('Address', 'address')}
        {field('Pre-existing conditions / allergies', 'preExistingCondition')}
        <button
          type="button"
          onClick={registerFamily}
          className="w-full bg-teal-600 text-white rounded-lg py-2 font-medium"
        >
          Register & link family member
        </button>
        {familyMsg ? <p className="text-sm text-slate-600">{familyMsg}</p> : null}
      </div>
    </div>
  );
}
